const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

export const initialStateNodeFilterState = {
  activity: [],
  hierarchy: [],
  integrity: [],
}

const HIGH_INTEGRITY_THRESHOLD = 80
const MEDIUM_INTEGRITY_THRESHOLD = 50

const HIERARCHY_LABELS = {
  empresa: "Empresa",
  zona: "Zona",
  localización: "Localización",
  sublocalización: "Sublocalización",
  automate: "Automate",
}

const HIERARCHY_ORDER = ["empresa", "zona", "localización", "sublocalización", "automate"]

const formatHierarchyLabel = (value) =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const collectNodeSearchValues = (node) => {
  const automateValues = (node?.automates || []).flatMap((automate) => [
    automate?.nombre,
    automate?.llave,
    automate?.producto,
    automate?.jerarquia_nombre,
  ])

  return [
    node?.nombre,
    node?.descripcion,
    node?.level_nombre,
    node?.producto,
    node?.product,
    node?.mac,
    node?.product_code,
    node?.firmware,
    node?.meter,
    node?.owner,
    node?.realtime?.llave,
    node?.device_info?.device_name,
    node?.device_info?.device_model,
    node?.device_info?.serial_number,
    node?.device_info?.device_location,
    node?.device_info?.user_email,
    ...automateValues,
  ]
}

const buildNodeSearchIndex = (node) =>
  normalizeSearchText(
    collectNodeSearchValues(node)
      .filter(Boolean)
      .join(" ")
  )

const getNodeHierarchyFilterValue = (node) => {
  if (node?.type === "automate") return "automate"
  return normalizeSearchText(node?.level_nombre)
}

const getNodeActivityFilterValue = (node, statusByNodeId, statusNodesByNodeId) => {
  if (node?.type === "automate") {
    if (typeof node?.activo === "boolean") return node.activo ? "activo" : "inactivo"
    if (typeof node?.active === "boolean") return node.active ? "activo" : "inactivo"
    return null
  }

  const connectivityStatus = statusNodesByNodeId?.[node?.id]?.online
  if (typeof connectivityStatus === "boolean") {
    return connectivityStatus ? "activo" : "inactivo"
  }

  const nodeStatus = statusByNodeId?.[node?.id] || node
  if (typeof nodeStatus?.online === "boolean") {
    return nodeStatus.online ? "activo" : "inactivo"
  }

  return null
}

const parseIntegrityValue = (value) => {
  const normalizedValue = String(value ?? "")
    .replace("%", "")
    .replace(",", ".")
    .trim()
  const parsedValue = parseFloat(normalizedValue)

  return Number.isFinite(parsedValue) ? Math.min(100, Math.max(0, parsedValue)) : null
}

const resolveNodeIntegrityValue = (node, nodeStatus) =>
  nodeStatus?.stats?.integridad ||
  node?.stats?.integridad ||
  nodeStatus?.powermate?.data_integrity ||
  node?.powermate?.data_integrity ||
  null

const getNodeIntegrityFilterValue = (node, statusByNodeId) => {
  const nodeStatus = statusByNodeId?.[node?.id] || node
  const integritySource = resolveNodeIntegrityValue(node, nodeStatus)

  const integrityValue = parseIntegrityValue(integritySource)

  if (integrityValue === null) return null
  if (integrityValue >= HIGH_INTEGRITY_THRESHOLD) return "alta"
  if (integrityValue >= MEDIUM_INTEGRITY_THRESHOLD) return "media"
  return "baja"
}

const matchesNodeFilters = (node, selectedFilters, statusByNodeId, statusNodesByNodeId) => {
  const hierarchyValue = getNodeHierarchyFilterValue(node)
  const activityValue = getNodeActivityFilterValue(node, statusByNodeId, statusNodesByNodeId)
  const integrityValue = getNodeIntegrityFilterValue(node, statusByNodeId)

  const matchesHierarchy =
    selectedFilters.hierarchy.length === 0 ||
    (hierarchyValue && selectedFilters.hierarchy.includes(hierarchyValue))

  const matchesActivity =
    selectedFilters.activity.length === 0 ||
    (activityValue && selectedFilters.activity.includes(activityValue))

  const matchesIntegrity =
    selectedFilters.integrity.length === 0 ||
    (integrityValue && selectedFilters.integrity.includes(integrityValue))

  return matchesHierarchy && matchesActivity && matchesIntegrity
}

export const buildStateFilterSections = (tree) => {
  const hierarchyValues = new Set()

  const walkTree = (nodes) => {
    ;(nodes || []).forEach((node) => {
      const hierarchyValue = getNodeHierarchyFilterValue(node)
      if (hierarchyValue) hierarchyValues.add(hierarchyValue)
      walkTree(node?.hijos)
    })
  }

  walkTree(tree)

  const hierarchyOptions = Array.from(hierarchyValues)
    .sort((leftValue, rightValue) => {
      const leftIndex = HIERARCHY_ORDER.indexOf(leftValue)
      const rightIndex = HIERARCHY_ORDER.indexOf(rightValue)

      if (leftIndex === -1 && rightIndex === -1) return leftValue.localeCompare(rightValue)
      if (leftIndex === -1) return 1
      if (rightIndex === -1) return -1
      return leftIndex - rightIndex
    })
    .map((value) => ({
      value,
      label: HIERARCHY_LABELS[value] || formatHierarchyLabel(value),
    }))

  return [
    {
      key: "activity",
      title: "Estado",
      options: [
        { value: "activo", label: "Online" },
        { value: "inactivo", label: "Offline" },
      ],
    },
    {
      key: "hierarchy",
      title: "Jerarquia",
      options: hierarchyOptions,
    },
    {
      key: "integrity",
      title: "Integridad",
      options: [
        { value: "baja", label: "Baja" },
        { value: "media", label: "Media" },
        { value: "alta", label: "Alta" },
      ],
    },
  ]
}

// Filtra el arbol manteniendo coincidencias y ancestros para que el resultado siga siendo navegable.
export const filterStateTreeBySearch = ({
  nodes,
  rawSearchTerm,
  selectedFilters = initialStateNodeFilterState,
  statusByNodeId,
  statusNodesByNodeId,
}) => {
  const normalizedSearchTerm = normalizeSearchText(rawSearchTerm)
  const hasActiveFilters = Object.values(selectedFilters).some((values) => values.length > 0)

  if (!normalizedSearchTerm && !hasActiveFilters) {
    return {
      filteredTree: nodes || [],
      expandedSearchNodeIds: new Set(),
      hasMatches: true,
    }
  }

  const expandedSearchNodeIds = new Set()

  const filterNode = (node) => {
    const filteredChildren = (node?.hijos || [])
      .map(filterNode)
      .filter(Boolean)

    const searchMatches = !normalizedSearchTerm || buildNodeSearchIndex(node).includes(normalizedSearchTerm)
    const filterMatches = matchesNodeFilters(node, selectedFilters, statusByNodeId, statusNodesByNodeId)
    const nodeMatches = searchMatches && filterMatches

    if (!nodeMatches && filteredChildren.length === 0) return null

    if (filteredChildren.length > 0 && node?.id !== undefined) {
      expandedSearchNodeIds.add(node.id)
    }

    return {
      ...node,
      hijos: filteredChildren,
    }
  }

  const filteredTree = (nodes || [])
    .map(filterNode)
    .filter(Boolean)

  return {
    filteredTree,
    expandedSearchNodeIds,
    hasMatches: filteredTree.length > 0,
  }
}

export const normalizeStateSearchTerm = normalizeSearchText
